import { useState } from 'react';
import { type WaitingForPresentationPageProps } from '../../../chulak/ChulakSession';
import EmbeddedStatusScreen from '../shared/StatusScreen';
import { useEmbeddedCopy } from '../../../hooks/useEmbeddedCopy';
import { type ViewTable } from '../../types';
import EmbeddedNotOpenedScreen from './NotOpenedScreen';
import EmbeddedPhoneInstallScreen from './PhoneInstallScreen';
import EmbeddedMissingInstallScreen from '../shared/MissingInstallScreen';

type View = 'waiting' | 'notOpened' | 'install' | 'missing';

/**
 * Continue in Yivi (phones): approve in the app on this phone. Cancel starts
 * over with a fresh request.
 */
export default function EmbeddedMobileWaitingPage({
  app,
  onBack,
  onRestart,
}: WaitingForPresentationPageProps) {
  const { t, pick } = useEmbeddedCopy();
  const [view, setView] = useState<View>('waiting');
  const appName = app?.name ?? '';

  const views: ViewTable<View> = {
    waiting: () => (
      <EmbeddedStatusScreen
        visual="app"
        app={app}
        badge="phone"
        title={t('embedded.waiting.title', { app: appName })}
        sub={t(pick('embedded.opening.sub', 'embedded.opening.subIssuance'))}
        secondary={{ label: t('embedded.cancel'), onClick: onRestart }}
        links={[
          {
            label: t('embedded.opening.help', { app: appName }),
            onClick: () => setView('notOpened'),
          },
        ]}
      />
    ),
    notOpened: () => (
      <EmbeddedNotOpenedScreen
        app={app}
        onInstall={() => setView('install')}
        onBack={() => setView('waiting')}
      />
    ),
    install: () => (
      <EmbeddedPhoneInstallScreen
        app={app}
        onOpenApp={onRestart}
        onNoStoreLink={() => setView('missing')}
        onBack={onBack}
      />
    ),
    missing: () => (
      <EmbeddedMissingInstallScreen
        app={app}
        onInstalled={onRestart}
        onBack={onBack}
      />
    ),
  };

  return views[view]();
}
